import React from "react";
import styles from "./SideDrawer.module.css";

export type AlertDisplay = {
  id: string;
  label: string;
  severity: string;
  message: string;
  timestamp?: number;
  mode?: "anchored" | "live";
};

type AlertsPanelProps = {
  alerts: AlertDisplay[];
  isOpen: boolean;
  alertsEnabled: boolean;
  onToggleEnabled: (next: boolean) => void;
  onClose: () => void;
};

const formatTimestamp = (timestamp?: number) => {
  if (!timestamp) {
    return "—";
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getSeverityClass = (severity: string) => {
  if (severity === "critical") {
    return styles.alertSeverityCritical;
  }
  if (severity === "warning") {
    return styles.alertSeverityWarning;
  }
  return styles.alertSeverityInfo;
};

const AlertsPanel = ({
  alerts,
  isOpen,
  alertsEnabled,
  onToggleEnabled,
  onClose,
}: AlertsPanelProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.alertsPanel}>
      <div className={styles.alertsPanelHeader}>
        <span>Alerts</span>
        <button type="button" className={styles.alertsClose} onClick={onClose}>
          ×
        </button>
      </div>
      <label className={styles.alertsToggle}>
        <input
          type="checkbox"
          checked={alertsEnabled}
          onChange={(event) => onToggleEnabled(event.target.checked)}
        />
        Enable alerts for this contract
      </label>
      {alerts.length ? (
        <ul className={styles.alertsList}>
          {alerts.map((alert) => (
            <li key={alert.id} className={styles.alertsItem}>
              <div className={styles.alertsItemHeader}>
                <span className={styles.alertsItemLabel}>{alert.label}</span>
                <span className={styles.alertsItemBadges}>
                  {alert.mode === "live" && (
                    <span className={styles.alertModeBadge}>Live</span>
                  )}
                  <span className={`${styles.alertsSeverity} ${getSeverityClass(alert.severity)}`}>
                    {alert.severity}
                  </span>
                </span>
              </div>
              <div className={styles.alertsTimestamp}>{formatTimestamp(alert.timestamp)}</div>
              <div className={styles.alertsMessage}>{alert.message}</div>
            </li>
          ))}
        </ul>
      ) : (
        <div className={styles.alertsEmpty}>No active alerts.</div>
      )}
    </div>
  );
};

export default AlertsPanel;
